import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOnboarding,
  type OnboardingMilestoneKey,
} from "../../api/onboarding";
import {
  CONTENT_MEMBERSHIP_ROLES,
  SITE_ADMIN_MEMBERSHIP_ROLES,
  hasActiveSite,
  hasMembershipRole,
} from "../../auth/access";
import { useAuth } from "../../auth/useAuth";
import { useSettings } from "../../hooks/useSettings";
import { DASHBOARD_TOUR_STEP_BY_ID, DASHBOARD_TOUR_STEPS } from "./steps";
import {
  createInitialDashboardTourSession,
  type DashboardTourPauseReason,
  type DashboardTourSessionState,
  type DashboardTourStepId,
} from "./schema";
import {
  clearDashboardTourSession,
  createDashboardTourDebouncedSaver,
  hasDashboardTourAutoStarted,
  isDashboardTourDisabledLocally,
  loadDashboardTourSession,
  markDashboardTourAutoStarted,
} from "./storage";
import { DashboardTourOverlay } from "./DashboardTourOverlay";
import type { DashboardTourContextData } from "./types";

function isFeatureFlagEnabled(): boolean {
  try {
    const env =
      (
        import.meta as {
          env?: Record<string, string | boolean | undefined>;
        }
      ).env ?? {};
    if (env.VITE_ONBOARDING_DASHBOARD_V1 === "1") {
      return true;
    }

    return env.DEV === "true" || env.DEV === true;
  } catch {
    return false;
  }
}

function generateSessionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `dbt-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

export interface DashboardTourApi {
  enabled: boolean;
  session: DashboardTourSessionState | null;
  start: () => void;
  restart: () => void;
  resume: () => void;
  dismiss: () => void;
  skip: () => void;
}

const noopApi: DashboardTourApi = {
  enabled: false,
  session: null,
  start: () => {},
  restart: () => {},
  resume: () => {},
  dismiss: () => {},
  skip: () => {},
};

const DashboardTourContext = createContext<DashboardTourApi>(noopApi);

export function useDashboardTour(): DashboardTourApi {
  return useContext(DashboardTourContext);
}

export interface DashboardTourProviderProps {
  children: ReactNode;
}

export function DashboardTourProvider({
  children,
}: DashboardTourProviderProps) {
  const { session: authSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const settingsQuery = useSettings();
  const userId = authSession?.user?.id ?? null;
  const tenantId = authSession?.activeTenant?.id ?? null;
  const role = authSession?.activeMembershipRole ?? null;
  const hasSite = hasActiveSite(authSession);
  const hasContentAccess = hasMembershipRole(
    authSession,
    CONTENT_MEMBERSHIP_ROLES,
  );
  const canManageWorkspace = hasMembershipRole(
    authSession,
    SITE_ADMIN_MEMBERSHIP_ROLES,
  );
  const canOpenSettings = canManageWorkspace && hasSite;

  const flagEnabled = isFeatureFlagEnabled();
  const locallyDisabled = isDashboardTourDisabledLocally();
  const active =
    flagEnabled &&
    !locallyDisabled &&
    Boolean(userId && tenantId) &&
    hasContentAccess &&
    (hasSite || canManageWorkspace);

  const onboardingQuery = useQuery({
    queryKey: ["dashboard-tour", "onboarding", tenantId],
    queryFn: () => getOnboarding(tenantId!),
    enabled: Boolean(active && tenantId),
    retry: false,
    staleTime: 30_000,
  });

  const [state, setState] = useState<DashboardTourSessionState | null>(null);
  const saverRef = useRef<ReturnType<
    typeof createDashboardTourDebouncedSaver
  > | null>(null);

  useEffect(() => {
    if (!active || !userId || !tenantId) {
      setState(null);
      saverRef.current = null;
      return;
    }

    saverRef.current = createDashboardTourDebouncedSaver();
    const loaded = loadDashboardTourSession(userId, tenantId);
    const untouchedWelcomeSession =
      loaded?.status === "active" &&
      loaded.currentStep === "welcome" &&
      loaded.startedAt !== null &&
      loaded.startedAt === loaded.lastActivityAt;

    if (loaded && untouchedWelcomeSession) {
      if (!hasDashboardTourAutoStarted(userId, tenantId)) {
        markDashboardTourAutoStarted(userId, tenantId);
      }
      clearDashboardTourSession(userId, tenantId);
      setState(null);
      return;
    }

    setState(loaded);
  }, [active, tenantId, userId]);

  useEffect(() => {
    if (!active || !state) return;
    saverRef.current?.save(state);
  }, [active, state]);

  useEffect(() => {
    if (!active) return;
    const flush = () => saverRef.current?.flushNow();
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [active]);

  const mutate = useCallback(
    (
      mutator: (
        prev: DashboardTourSessionState | null,
      ) => DashboardTourSessionState | null,
    ) => {
      setState((prev) => mutator(prev));
    },
    [],
  );

  const touch = useCallback(
    (session: DashboardTourSessionState): DashboardTourSessionState => ({
      ...session,
      lastActivityAt: nowIso(),
    }),
    [],
  );

  const beginNewSession = useCallback(() => {
    if (!userId || !tenantId) return;

    setState({
      ...createInitialDashboardTourSession(
        userId,
        tenantId,
        generateSessionId(),
      ),
      status: "active",
      startedAt: nowIso(),
      lastActivityAt: nowIso(),
    });
  }, [tenantId, userId]);

  const start = useCallback(() => {
    beginNewSession();
  }, [beginNewSession]);

  const restart = useCallback(() => {
    beginNewSession();
  }, [beginNewSession]);

  const resume = useCallback(() => {
    mutate((prev) => {
      if (!prev || prev.status !== "paused") return prev;
      return touch({ ...prev, status: "active", pauseReason: null });
    });
  }, [mutate, touch]);

  const dismiss = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      return touch({ ...prev, status: "dismissed", pauseReason: null });
    });
  }, [mutate, touch]);

  const skip = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      return touch({ ...prev, status: "skipped", pauseReason: null });
    });
  }, [mutate, touch]);

  const ctxRef = useRef<DashboardTourContextData | null>(null);

  const milestoneDone = useCallback(
    (key: OnboardingMilestoneKey) => {
      const milestone = onboardingQuery.data?.milestones.find(
        (item) => item.key === key,
      );
      return milestone?.completed === true;
    },
    [onboardingQuery.data?.milestones],
  );

  const getNextStepId = useCallback((from: DashboardTourStepId) => {
    const ctx = ctxRef.current;
    if (!ctx) return null;

    const startIndex = DASHBOARD_TOUR_STEPS.findIndex(
      (step) => step.id === from,
    );
    if (startIndex < 0) return null;

    for (
      let index = startIndex + 1;
      index < DASHBOARD_TOUR_STEPS.length;
      index += 1
    ) {
      const candidate = DASHBOARD_TOUR_STEPS[index];
      if (!candidate) continue;
      if (candidate.isRelevant && !candidate.isRelevant(ctx)) {
        continue;
      }
      return candidate.id;
    }

    return null;
  }, []);

  const advanceTo = useCallback(
    (stepId: DashboardTourStepId) => {
      mutate((prev) => {
        if (!prev) return prev;
        return touch({
          ...prev,
          currentStep: stepId,
          status: "active",
          pauseReason: null,
        });
      });
    },
    [mutate, touch],
  );

  const completeTour = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      return touch({
        ...prev,
        status: "completed",
        completedAt: prev.completedAt ?? nowIso(),
        pauseReason: null,
      });
    });
  }, [mutate, touch]);

  const advance = useCallback(() => {
    mutate((prev) => {
      if (!prev) return prev;
      const next = getNextStepId(prev.currentStep);
      if (!next) {
        return touch({
          ...prev,
          status: "completed",
          completedAt: prev.completedAt ?? nowIso(),
          pauseReason: null,
        });
      }
      return touch({
        ...prev,
        currentStep: next,
        status: "active",
        pauseReason: null,
      });
    });
  }, [getNextStepId, mutate, touch]);

  const pauseAt = useCallback(
    (
      reason: Exclude<DashboardTourPauseReason, null>,
      stepId: DashboardTourStepId,
    ) => {
      mutate((prev) => {
        if (!prev) return prev;
        return touch({
          ...prev,
          currentStep: stepId,
          status: "paused",
          pauseReason: reason,
        });
      });
    },
    [mutate, touch],
  );

  const navigateTo = useCallback(
    (to: string) => {
      navigate(to);
    },
    [navigate],
  );

  const stepCtx = useMemo<DashboardTourContextData>(
    () => ({
      session: state,
      pathname: location.pathname,
      userId,
      tenantId,
      siteId: authSession?.activeSite?.id ?? null,
      role,
      hasActiveSite: hasSite,
      hasContentAccess,
      canManageWorkspace,
      canOpenSettings,
      onboarding: onboardingQuery.data ?? null,
      settings: settingsQuery.data ?? null,
      localesEnabled: settingsQuery.data?.activeLocales ?? [],
      milestoneDone,
      navigate: navigateTo,
      advance,
      advanceTo,
      pauseAt,
      completeTour,
      dismissTour: dismiss,
      skipTour: skip,
      getNextStepId,
    }),
    [
      advance,
      advanceTo,
      authSession?.activeSite?.id,
      canManageWorkspace,
      canOpenSettings,
      completeTour,
      dismiss,
      getNextStepId,
      hasContentAccess,
      hasSite,
      location.pathname,
      milestoneDone,
      navigateTo,
      onboardingQuery.data,
      pauseAt,
      role,
      settingsQuery.data,
      skip,
      state,
      tenantId,
      userId,
    ],
  );

  useEffect(() => {
    ctxRef.current = stepCtx;
  }, [stepCtx]);

  const autoStartKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !userId || !tenantId) return;
    if (state) {
      autoStartKeyRef.current = `${userId}:${tenantId}`;
      return;
    }
    if (onboardingQuery.isLoading) return;

    const autoStartKey = `${userId}:${tenantId}`;
    if (autoStartKeyRef.current === autoStartKey) return;
    autoStartKeyRef.current = autoStartKey;

    if (onboardingQuery.isError) return;
    if (onboardingQuery.data?.completedAt) return;
    if (hasDashboardTourAutoStarted(userId, tenantId)) return;

    markDashboardTourAutoStarted(userId, tenantId);
    beginNewSession();
  }, [
    active,
    beginNewSession,
    onboardingQuery.data?.completedAt,
    onboardingQuery.isError,
    onboardingQuery.isLoading,
    state,
    tenantId,
    userId,
  ]);

  useEffect(() => {
    if (!state || state.status !== "paused") return;
    if (state.pauseReason === "site_required" && hasSite) {
      resume();
      return;
    }
    if (
      state.pauseReason === "editor_handoff" &&
      location.pathname !== "/pages/new"
    ) {
      resume();
    }
  }, [hasSite, location.pathname, resume, state]);

  const suppressOverlay = location.pathname === "/pages/new";
  const currentStep = state
    ? DASHBOARD_TOUR_STEP_BY_ID[state.currentStep]
    : null;

  useEffect(() => {
    if (!state || state.status !== "active" || suppressOverlay) return;
    if (!currentStep) {
      completeTour();
      return;
    }
    if (currentStep.isRelevant && !currentStep.isRelevant(stepCtx)) {
      advance();
    }
  }, [advance, completeTour, currentStep, state, stepCtx, suppressOverlay]);

  const visibleSteps = useMemo(
    () =>
      DASHBOARD_TOUR_STEPS.filter(
        (step) => !step.isRelevant || step.isRelevant(stepCtx),
      ),
    [stepCtx],
  );

  const currentStepLabel = useMemo(() => {
    if (!currentStep) return undefined;
    const index = visibleSteps.findIndex((step) => step.id === currentStep.id);
    if (index < 0) return undefined;
    return `Step ${index + 1} of ${visibleSteps.length}`;
  }, [currentStep, visibleSteps]);

  const api = useMemo<DashboardTourApi>(
    () => ({
      enabled: active,
      session: state,
      start,
      restart,
      resume,
      dismiss,
      skip,
    }),
    [active, dismiss, restart, resume, skip, start, state],
  );

  return (
    <DashboardTourContext.Provider value={api}>
      {children}
      {active &&
      state &&
      state.status === "active" &&
      currentStep &&
      !suppressOverlay ? (
        <DashboardTourOverlay
          step={currentStep}
          ctx={stepCtx}
          stepLabel={currentStepLabel}
          onAdvance={advance}
          onDismissTour={dismiss}
          onSkipTour={skip}
        />
      ) : null}
    </DashboardTourContext.Provider>
  );
}
